/* eslint-disable jira/integration/test-id-by-folder-structure */
/** @jsx jsx */
import React, {
	forwardRef,
	useMemo,
	type ReactNode,
	type ComponentPropsWithRef,
	type RefAttributes,
} from 'react';
import { css, styled as styled2, jsx } from '@compiled/react';
// eslint-disable-next-line jira/restricted/styled-components-migration, @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled, { keyframes, type StyledComponentClass } from 'styled-components';
import ExpandIcon from '@atlaskit/icon/core/chevron-down';
import Button from '@atlaskit/button';
import DropdownMenu from '@atlaskit/dropdown-menu';

import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { expVal } from '@atlassian/jira-feature-experiments';
import {
	PRODUCT_DISCOVERY_PROJECT,
	type ProjectType,
	SERVICE_DESK_PROJECT,
	SOFTWARE_PROJECT,
	CUSTOMER_SERVICE_PROJECT,
} from '@atlassian/jira-common-constants/src/project-types.tsx';
import { useIntl, type MessageDescriptor } from '@atlassian/jira-intl';
import { fg } from '@atlassian/jira-feature-gating';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
// eslint-disable-next-line jira/import/no-wildcard-imports
import * as i18n from '../../common/messages.tsx';
import type { Props as BaseProps } from '../../common/types.tsx';
import {
	type Props as LinkWithAnalyticsProps,
	LinkWithAnalytics,
} from '../../common/ui/link/index.tsx';
import { DropdownMenuContent } from './dropdown-content/index.tsx';
import ErrorIcon from './error-icon/index.tsx';
import HeaderIcon from './header-icon/index.tsx';
import { useEffectFireErrorCallback } from './utils.tsx';

const ToolTipContainer = forwardRef<HTMLDivElement>((props, ref) => (
	<FilterTitleToolipContainer ref={ref} {...props} />
));

export type Props = {
	project: BaseProps['project'];
	landingUrl?: string | null;
	children: ReactNode;
};

const getRebrandedProjectTypeMessage = (isSimplified: boolean, projectType: ProjectType) => {
	if (projectType === CUSTOMER_SERVICE_PROJECT && fg('jcs_project_type_m3')) {
		return i18n.boards.customerService;
	}

	if (projectType === SOFTWARE_PROJECT) {
		return isSimplified ? i18n.boards.teamManagedSoftware : i18n.boards.companyManagedSoftware;
	}
	if (projectType === SERVICE_DESK_PROJECT) {
		return isSimplified
			? i18n.boards.teamManagedServiceManagement
			: i18n.boards.companyManagedServiceManagement;
	}
	if (projectType === PRODUCT_DISCOVERY_PROJECT) {
		return i18n.boards.productDiscovery;
	}
	return isSimplified ? i18n.boards.teamManagedBusiness : i18n.boards.companyManagedBusiness;
};

export const getBoardTypeMessage = (
	isSimplified: boolean,
	projectType: string | ProjectType,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	formatMessage: (MessageDescriptor: MessageDescriptor, values?: any) => string,
): string => {
	// TODO: All that projectTypeMsg seems to be very legacy, we can get rid of it when cleaning jcs_project_type_m3
	if (fg('jcs_project_type_m3')) {
		// @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'ProjectType'.
		return formatMessage(getRebrandedProjectTypeMessage(isSimplified, projectType));
	}

	// @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'ProjectType'.
	const boardTypeMessage = getRebrandedProjectTypeMessage(isSimplified, projectType);

	let projectTypeMsg = '';
	if (projectType === 'service_desk') {
		projectTypeMsg = formatMessage(i18n.types.service_desk);
	} else if (projectType) {
		// @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ product_discovery: MessageDescriptor; software: MessageDescriptor; service_desk: MessageDescriptor; business: MessageDescriptor; }'.
		projectTypeMsg = formatMessage(i18n.types[projectType]);
	}

	return projectTypeMsg ? formatMessage(boardTypeMessage, { projectType: projectTypeMsg }) : '';
};

const getDropdownTitle = (projectType: ProjectType) => {
	if (
		projectType === SERVICE_DESK_PROJECT ||
		(projectType === CUSTOMER_SERVICE_PROJECT && fg('csm_rename_jcs_to_csm'))
	) {
		return i18n.general.queue;
	}
	if (projectType === PRODUCT_DISCOVERY_PROJECT) {
		return i18n.general.views;
	}

	return i18n.general.board;
};

export const DeepDiveCard = ({
	project: { projectName, projectType, avatarUrl, href, isSimplified, barColor },
	landingUrl,
	children,
}: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const boardTypeMessage = getBoardTypeMessage(isSimplified, projectType, formatMessage);

	const cardHeader = (
		<Header
			href={landingUrl ?? href}
			actionSubject="card"
			actionSubjectId="deepDiveProjectCard"
			attributes={{
				projectType,
				nextGen: isSimplified,
			}}
		>
			<HeaderIcon url={avatarUrl} width={24} height={24} />
			<Heading>
				<ProjectNameHeading data-testid="deep-dive-card-content--project-name-heading">
					{projectName}
				</ProjectNameHeading>
				<ProjectTypeSubheading data-testid="deep-dive-card-content--project-type-subheading">
					{boardTypeMessage}
				</ProjectTypeSubheading>
			</Heading>
		</Header>
	);

	return (
		// @ts-expect-error - TS2322 - Type 'string | undefined' is not assignable to type 'string'.
		<StyledContainer barColor={barColor}>
			<Tooltip content={projectName} position="top">
				{cardHeader}
			</Tooltip>
			{children}
		</StyledContainer>
	);
};
DeepDiveCard.whyDidYouRender = true;

const ErrorSkeletonContainer = ({
	message,
	hideMessage,
}: {
	message: MessageDescriptor;
	hideMessage?: boolean;
}) => {
	const { formatMessage } = useIntl();
	const formattedMessage = formatMessage(message);
	return (
		<Tooltip
			content={formattedMessage}
			position="mouse"
			mousePosition="top"
			// @ts-expect-error - TS2322 - Type 'StyledComponentClass<ClassAttributes<HTMLDivElement> & HTMLAttributes<HTMLDivElement> & { height?: string | number | undefined; }, any, ClassAttributes<...> & ... 1 more ... & { ...; }>' is not assignable to type 'keyof IntrinsicElements | ComponentType<AllHTMLAttributes<HTMLElement> & { ref: Ref<HTMLElement>; }> | undefined'.
			tag={ErrorSkeleton}
			truncate
		>
			<>
				{hideMessage !== true && <ErrorMessage>{formattedMessage}</ErrorMessage>}
				<ErrorIcon />
			</>
		</Tooltip>
	);
};

type BoardsDropdownProps = {
	project: BaseProps['project'];
	boards: BaseProps['boards'];
	onError: () => void;
};
export const BoardsDropdown = ({
	project,
	boards: { loading, error, data },
	onError,
}: BoardsDropdownProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { projectType } = project;
	useEffectFireErrorCallback(error, onError);

	const errorGalaxia = expVal('project-terminology-refresh', 'isEnabled', false)
		? i18n.state.errorGalaxia
		: i18n.state.error;

	const [items, itemCount] = useMemo(() => {
		const dataItems = data?.items || [];
		// Children items are section headers, we only want to count the children, not the section headers
		const count = dataItems.reduce(
			(total, item) => (item.children ? total + item.children.length : total + 1),
			0,
		);
		return [dataItems, count];
	}, [data]);

	if (error === true) {
		return (
			<BoardContentRow data-vc="deep-dive-card-filter-shortcuts-board-content-row-with-error">
				<ErrorSkeletonContainer message={errorGalaxia} />
			</BoardContentRow>
		);
	}
	if (loading === true || !data) {
		return (
			<BoardContentRow data-vc="deep-dive-card-filter-shortcuts-board-content-loading">
				{/* @ts-expect-error - TS2322 - Type '{ percentageWidth: number; }' is not assignable to type 'IntrinsicAttributes & IntrinsicClassAttributes<Component<ThemedOuterStyledProps<ClassAttributes<HTMLDivElement> & HTMLAttributes<HTMLDivElement> & { ...; }, any>, any, any>> & Readonly<...> & Readonly<...>'. */}
				<LoadingBarSkeleton percentageWidth={75} />
				<LoadingBarSkeleton invisible />
			</BoardContentRow>
		);
	}

	return (
		<BoardContentRow data-vc="deep-dive-card-filter-shortcuts-board-content-row">
			<DropdownMenu
				testId="deep-dive-cards-section.ui.deep-dive-card.dropdown"
				trigger={({ triggerRef, ...triggerProps }) => (
					<BoardCounterButton
						{...triggerProps}
						appearance="subtle"
						// @ts-expect-error - TS2322 - Type 'Element | null' is not assignable to type 'ReactChild | undefined'.
						iconAfter={itemCount ? <ExpandIcon spacing="compact" size="small" label="" /> : null}
						isDisabled={!itemCount}
						spacing="none"
						testId="deep-dive-cards-section.ui.deep-dive-card.deep-dive-card-board-content-dropdown-trigger"
						// @ts-expect-error - TS2322: Type 'Ref<HTMLElement>' is not assignable to type '((instance: any) => void) | RefObject<SVGElement | HTMLElement | Component<{}, {}, any>> | undefined'
						innerRef={triggerRef}
					>
						<DropdownTitle
							numItems={itemCount}
							data-testid="deep-dive-card--board-content--dropdown--title"
							jsvDarkmodeInnovationEnabled
						>
							{formatMessage(getDropdownTitle(projectType), {
								numberBoards: itemCount,
							})}
						</DropdownTitle>
					</BoardCounterButton>
				)}
				spacing="compact"
				shouldRenderToParent
			>
				<DropdownMenuContent data={items} project={project} />
			</DropdownMenu>
		</BoardContentRow>
	);
};

type FilterShortcutsProps = {
	project: BaseProps['project'];
	filters: BaseProps['filters'];
	onError: () => void;
};
export const FilterShortcuts = ({
	project: { projectType, isSimplified },
	filters: { loading, error, data },
	onError,
}: FilterShortcutsProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const errorGalaxia = expVal('project-terminology-refresh', 'isEnabled', false)
		? i18n.state.errorGalaxia
		: i18n.state.error;
	useEffectFireErrorCallback(error, onError);

	if (error === true && !data) {
		return (
			<FilterShortCutsContainer>
				<ContentRow data-vc="deep-dive-card-filter-shortcuts-content-row-with-errorg">
					<ErrorSkeletonContainer message={errorGalaxia} />
				</ContentRow>
			</FilterShortCutsContainer>
		);
	}
	if (!data) {
		if (loading !== true && !__SERVER__) return <FilterShortCutsContainer />;
		return (
			<FilterShortCutsContainer>
				<ContentRow data-vc="deep-dive-card-filter-shortcuts-content-row-loading-0">
					{/* @ts-expect-error - TS2322 - Type '{ percentageWidth: number; }' is not assignable to type 'IntrinsicAttributes & IntrinsicClassAttributes<Component<ThemedOuterStyledProps<ClassAttributes<HTMLDivElement> & HTMLAttributes<HTMLDivElement> & { ...; }, any>, any, any>> & Readonly<...> & Readonly<...>'. */}
					<LoadingBarSkeleton percentageWidth={75} />
					<LoadingBarSkeleton invisible />
				</ContentRow>
				<ContentRow data-vc="deep-dive-card-filter-shortcuts-content-row-loading-1">
					{/* @ts-expect-error - TS2322 - Type '{ percentageWidth: number; }' is not assignable to type 'IntrinsicAttributes & IntrinsicClassAttributes<Component<ThemedOuterStyledProps<ClassAttributes<HTMLDivElement> & HTMLAttributes<HTMLDivElement> & { ...; }, any>, any, any>> & Readonly<...> & Readonly<...>'. */}
					<LoadingBarSkeleton percentageWidth={75} />
					<LoadingBarSkeleton />
				</ContentRow>
				<ContentRow data-vc="deep-dive-card-filter-shortcuts-content-row-loading-2">
					{/* @ts-expect-error - TS2322 - Type '{ percentageWidth: number; }' is not assignable to type 'IntrinsicAttributes & IntrinsicClassAttributes<Component<ThemedOuterStyledProps<ClassAttributes<HTMLDivElement> & HTMLAttributes<HTMLDivElement> & { ...; }, any>, any, any>> & Readonly<...> & Readonly<...>'. */}
					<LoadingBarSkeleton percentageWidth={75} />
					<LoadingBarSkeleton />
				</ContentRow>
			</FilterShortCutsContainer>
		);
	}
	const links = data.map<ReactNode>((item, idx) => {
		const loadingTitle = item.isLoading === true && !item.content;
		const loadingItemCounts = item.isLoading;
		const itemCountsError = item.error;
		const renderItemCounts = () => {
			if (loadingItemCounts === true) {
				return <LoadingBarSkeleton />;
			}
			if (itemCountsError === true) {
				return <ErrorSkeletonContainer hideMessage message={i18n.state.errorCount} />;
			}
			return item.itemCount !== undefined && item.itemCount >= 0 ? (
				<FilterCountBadge
					count={item.itemCount}
					data-testid="deep-dive-card-content--dropdown--content--filter-link--count-badge"
				/>
			) : null;
		};
		return (
			<ContentRow key={idx} data-vc={`deep-dive-card-filter-shortcuts-content-row-${idx}`}>
				{/*
					This is a fix to address the a11y violation of filter item loading state (BLU-8306 & ENGHEALTH-39115).
					Technically, skeletons should not have hrefs in the first place, and hence should NOT be an <a/>,
					but the skeleton was under filter link, which caused an a11y violation of no link name for skeletons.
				*/}
				{loadingTitle === true ? (
					<>
						{/* @ts-expect-error - TS2322 - Type '{ percentageWidth: number; }' is not assignable to type 'IntrinsicAttributes & IntrinsicClassAttributes<Component<ThemedOuterStyledProps<ClassAttributes<HTMLDivElement> & HTMLAttributes<HTMLDivElement> & { ...; }, any>, any, any>> & Readonly<...> & Readonly<...>'. */}
						<LoadingBarSkeleton percentageWidth={75} />
						{renderItemCounts()}
					</>
				) : (
					<FilterLink
						href={item.href}
						// @ts-expect-error - TS2322 - Type '{ children: (Element | null)[]; href: string; loading: boolean | undefined; actionSubjectId: string; attributes: { projectType: ProjectType; nextGen: boolean; }; "data-testid": string; }' is not assignable to type 'IntrinsicAttributes & IntrinsicClassAttributes<Component<ThemedOuterStyledProps<WithOptionalTheme<AnchorHTMLAttributes<HTMLAnchorElement> & { children: ReactNode; ... 10 more ...; prefetch?: false | ... 2 more ... | undefined; } & { ...; }, any>, any>, any, any>> & Readonly<...> & Readonly<...>'.
						loading={loadingItemCounts}
						actionSubjectId="deepDiveCardLinkFilter"
						attributes={{
							projectType,
							nextGen: isSimplified,
						}}
						data-testid="deep-dive-card-content--dropdown--content--filter-link"
						aria-label={item.content}
					>
						<Tooltip
							content={item.content}
							position="bottom"
							// @ts-expect-error - TS2322 - Type 'ForwardRefExoticComponent<RefAttributes<unknown>>' is not assignable to type 'keyof IntrinsicElements | ComponentType<AllHTMLAttributes<HTMLElement> & { ref: Ref<HTMLElement>; }> | undefined'.
							tag={ToolTipContainer}
							truncate
							testId="deep-dive-cards-section.ui.deep-dive-card.deep-dive-card-content-dropdown-content-filter-link-tool-tip-container"
						>
							<FilterTitle data-testId="deep-dive-card-content--dropdown--content--filter-link--tool-tip-container--filter-title">
								{item.content}
							</FilterTitle>
						</Tooltip>
						{renderItemCounts()}
					</FilterLink>
				)}
			</ContentRow>
		);
	});
	return (
		<FilterShortCutsContainer>
			<ContentRow data-vc="deep-dive-card-filter-shortcuts-content-row-heading">
				<FilterLinksHeading>
					{formatMessage(
						projectType === SERVICE_DESK_PROJECT ||
							(projectType === CUSTOMER_SERVICE_PROJECT && fg('csm_rename_jcs_to_csm'))
							? i18n.heading.queuesHeading
							: i18n.heading.boardsHeading,
					)}
				</FilterLinksHeading>
			</ContentRow>
			{links}
		</FilterShortCutsContainer>
	);
};

type ButtonProps = ComponentPropsWithRef<typeof Button>;
/**
 * The amount content item borders 'flow' into the left gutter area.
 */
const gutterLeftRightPullback = 4;

/**
 * The amount board item counts 'flow' into the top gutter area separating the heading.
 */
const gutterTopPullback = 4 * (1 / 4);

/**
 * Top and bottom padding for computing hover state backgrounds.
 */
const hoverStateTopBottomPadding = 4 * (3 / 4);

// TODO: migrate to object syntax. Autofix is available for many cases. Remove the eslint-disable for @atlaskit/design-system/no-styled-tagged-template-expression to check.
// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled, jira/styled/styled-component-order -- To migrate as part of go/ui-styling-standard
const StyledContainer = styled.div<{ barColor: string }>`
	display: flex;
	height: 168px;
	width: 240px;
	padding-top: ${token('space.200')};
	flex-direction: column;
	color: inherit;
	background: ${token('elevation.surface.raised')};
	:not(:first-of-type) {
		margin-left: ${token('space.150')};
	}
	border: none;
	${'' /* eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage */}
	border-radius: ${token('radius.small', '3px')};
	border-left: 20px solid
		${
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(props: any) => props.barColor || token('color.background.neutral')
		};
	box-sizing: border-box;
	box-shadow: ${token('elevation.shadow.raised')};
	transition:
		box-shadow 0.3s ease-in-out,
		border-color 0.6s ease-in;
	:hover {
		box-shadow: ${token('elevation.shadow.overlay')};
	}
`;

// TODO: migrate to object syntax. Autofix is available for many cases. Remove the eslint-disable for @atlaskit/design-system/no-styled-tagged-template-expression to check.
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, jira/styled/styled-component-order -- To migrate as part of go/ui-styling-standard
const Heading = styled.div({
	display: 'inline-block',
	verticalAlign: 'top',
	flex: 1,
	marginLeft: token('space.100'),
	marginRight: token('space.050'),
	overflow: 'hidden',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, jira/styled/styled-component-order
const ProjectNameHeadingOld = styled.p({
	font: token('font.heading.xsmall'),
	color: token('color.text'),
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	margin: 0,
	height: '16px',
});

const ProjectNameHeadingNew = ({ children }: { children: ReactNode }) => (
	<div css={projectNameHeadingStyles}>{children}</div>
);

const projectNameHeadingStyles = css({
	font: token('font.heading.xsmall'),
	color: token('color.text'),
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	margin: 0,
	height: '18px',
});

const ProjectNameHeading = componentWithCondition(
	() => fg('jira_kuro_jjj_for_you_project_name_cropped_card'),
	ProjectNameHeadingNew,
	ProjectNameHeadingOld,
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ProjectTypeSubheading = styled2.p({
	font: token('font.body.small'),
	color: token('color.text'),
	margin: 0,
	textOverflow: 'ellipsis',
	overflow: 'hidden',
	whiteSpace: 'nowrap',
	textTransform: 'none',
});

// TODO: migrate to object syntax. Autofix is available for many cases. Remove the eslint-disable for @atlaskit/design-system/no-styled-tagged-template-expression to check.
// eslint-disable-next-line  @typescript-eslint/no-explicit-any, @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DropdownTitle = styled.p<{ numItems?: any; jsvDarkmodeInnovationEnabled?: boolean }>`
	display: flex;
	text-transform: none;
	font: ${token('font.body.small')};
	/* Remove custom text colour css if jsvDarkmodeInnovationEnabled ff is on */
	${({ jsvDarkmodeInnovationEnabled }) =>
		!jsvDarkmodeInnovationEnabled && `font-weight: ${token('font.weight.semibold')};`}
	${({ numItems, jsvDarkmodeInnovationEnabled }) =>
		!jsvDarkmodeInnovationEnabled &&
		`color: ${numItems ? token('color.text') : token('color.text.subtlest')};`}
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentRow = styled.div({
	display: 'flex',
	paddingLeft: token('space.200'),
	paddingRight: token('space.150'),
	marginBottom: token('space.025'),
});

const shimmer = keyframes({
	'0%': {
		backgroundPosition: '-300px 0',
	},
	'100%': {
		backgroundPosition: '1000px 0',
	},
});

// TODO: migrate to object syntax. Autofix is available for many cases. Remove the eslint-disable for @atlaskit/design-system/no-styled-tagged-template-expression to check.
// eslint-disable-next-line @typescript-eslint/no-explicit-any,  @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LoadingBarSkeleton = styled.div<{ height?: string | number; invisible?: any }>`
	display: inline-block;
	height: ${
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(props: any) => (props.height ? `${props.height}px` : '22px')
	};
	animation-duration: 0.85s;
	animation-fill-mode: forwards;
	animation-iteration-count: infinite;
	animation-name: ${shimmer};
	animation-timing-function: linear;
	background-color: ${token('color.background.neutral')};
	background-image: linear-gradient(
		to right,
		${token('color.background.neutral')} 10%,
		${token('color.background.neutral')} 20%,
		${token('color.background.neutral')} 30%
	);
	background-repeat: no-repeat;
	border-radius: ${token('radius.small')};
	visibility: ${
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(props: any) => (props.invisible ? 'hidden' : 'visible')
	};

	/* These styles look weird on purpose
       See: https://github.com/emotion-js/emotion/issues/1105
       ]We need to setup something like: https://emotion.sh/docs/ssr#on-client
       At the moment these styles will only work for two skeletons and do not scale
    */
	:not(:last-child),
	:only-child {
		flex: 1;
	}

	:last-child {
		margin-left: ${token('space.200')};
		width: 24px;
		margin-right: ${token('space.050')};
	}
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const FilterLinksHeading = styled.p({
	font: token('font.body.small'),
	fontWeight: token('font.weight.semibold'),
	color: token('color.text.subtlest'),
	paddingLeft: token('space.050'),
	paddingBottom: token('space.050'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FilterTitleToolipContainer = styled2.div({
	overflow: 'hidden',
	flex: 1,
});

// TODO: migrate to object syntax. Autofix is available for many cases. Remove the eslint-disable for @atlaskit/design-system/no-styled-tagged-template-expression to check.
// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FilterTitle = styled.p`
	font: ${token('font.body.small')};
	text-transform: none;
	color: ${token('color.text')};
	text-overflow: ellipsis;
	white-space: nowrap;
	overflow: hidden;
	padding: ${token('space.050')} 0;
	padding-left: ${token('space.050')};
`;

// TODO: migrate to object syntax. Autofix is available for many cases. Remove the eslint-disable for @atlaskit/design-system/no-styled-tagged-template-expression to check.
// eslint-disable-next-line @typescript-eslint/no-explicit-any,  @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FilterCountBadge = styled.div<{ count: any }>`
	font: ${token('font.body.small')};
	box-sizing: border-box;
	background: ${token('color.background.neutral')};
	align-content: center;
	padding: 1px ${token('space.025')} 0;
	border-radius: ${token('radius.full')};
	text-transform: none;
	margin: ${hoverStateTopBottomPadding}px;
	width: 30px;
	min-width: 30px;
	height: 16px;
	text-align: center;
	color: ${token('color.text')};

	::after {
		content: '${({ count }) => (parseInt(count, 10) > 99 ? '99+' : count)}';
	}
`;

// TODO: migrate to object syntax. Autofix is available for many cases. Remove the eslint-disable for @atlaskit/design-system/no-styled-tagged-template-expression to check.
// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ErrorSkeleton = styled.div<{ height?: string | number }>`
	font: ${token('font.body.small')};
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-top: 0;
	text-transform: none;
	height: ${
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(props: any) => (props.height ? `${props.height}px` : '22px')
	};
	background-color: ${token('color.background.neutral')};
	border-radius: ${token('radius.small')};
	max-width: 100%;
	width: 100%;
	overflow-y: hidden;

	/* These styles look weird on purpose
       See: https://github.com/emotion-js/emotion/issues/1105
       ]We need to setup something like: https://emotion.sh/docs/ssr#on-client
       At the moment these styles will only work for two skeletons and do not scale
    */
	:not(:last-child),
	:only-child {
		padding: 0 ${token('space.050')};
	}

	:last-child:not(:only-child) {
		margin-left: ${token('space.200')};
		width: 24px;
		justify-content: center;
	}
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FilterShortCutsContainer = styled2.div({
	flex: 1,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ErrorMessage = styled2.div({
	flex: 1,
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	overflow: 'hidden',
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any,  @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const Header: StyledComponentClass<LinkWithAnalyticsProps, any> = styled(LinkWithAnalytics)({
	textAlign: 'left',
	color: 'inherit',
	width: '100%',
	/* Total margin needed is 28px. We reserve 14px to the subheading for line height.
	 */
	marginBottom: token('space.150'),
	/* Pull the header back one grid space, as it must partly overlap with the left border bar */
	marginLeft: `-${8 + gutterLeftRightPullback}px`,
	display: 'flex',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':hover': {
		color: 'inherit',
		textDecoration: 'none',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const BoardContentRow = styled(ContentRow)({
	borderTop: `1px solid ${token('color.border')}`,
	margin: 0,
	height: '28px',
	alignItems: 'center',
	paddingRight: token('space.150'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const BoardCounterButton: StyledComponentClass<
	ButtonProps & RefAttributes<HTMLElement>,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	any
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
> = styled(Button)({
	font: token('font.body.small'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	paddingTop: `${gutterTopPullback + 2}px !important`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	paddingBottom: `${hoverStateTopBottomPadding}px !important`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	paddingLeft: `${token('space.050')} !important`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/ui-styling-standard/no-styled
export const BoardCounterText = styled.div({
	font: token('font.heading.xxsmall'),
	color: token('color.text.subtlest'),
	fontWeight: token('font.weight.bold'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
	paddingLeft: `${token('space.050')} !important`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
	'&:first-of-type': {
		marginTop: token('space.100'),
		marginBottom: token('space.100'),
	},
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any,  @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const BoardDropdownLink: StyledComponentClass<LinkWithAnalyticsProps, any> = styled(
	LinkWithAnalytics,
)({
	font: token('font.body.small'),
	display: 'inline-block',
	color: 'inherit',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':hover': {
		color: 'inherit',
		textDecoration: 'none',
	},
	padding: token('space.050'),
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any,  @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const FilterLink: StyledComponentClass<LinkWithAnalyticsProps, any> = styled(
	LinkWithAnalytics,
)`
	font: ${token('font.body.small')};
	flex: 1;
	display: flex;
	max-width: 100%;
	justify-content: space-between;
	align-items: center;
	${'' /* eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage */}
	border-radius: ${token('radius.small', '3px')};
	text-decoration: none !important;
	padding: 0;
	&:hover {
		background-color: ${token('color.background.neutral.hovered')};
	}

	/* Remove left margin of first loading skeleton if shown inside a FilterLink
     * We already apply an equivalent padding here
     * Comment changes what stylelint interpolates as the default value breaks their own checks...
     */
	> ${/* sc-custom '.stylelint-bypass-custom-substitution' */ LoadingBarSkeleton}:not(:last-child) {
		margin-left: 0;
	}
`;
